import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { requireAuth } from '../features/auth/guards.js';
import { PageHeader } from '@/components/account/PageHeader.js';
import { MyClaimsList } from '@/components/promo/MyClaimsList.js';
import { SpinHistoryList } from '@/components/spin/SpinHistoryList.js';
import { fetchMyClaims, PROMO_QUERY_KEYS } from '@/features/promo/api.js';
import { bucketOf } from '@/features/promo/claim-buckets.js';
import { HISTORY_TABS, promoGroupOf, type HistoryTab } from '@/features/promo/list-visibility.js';
import { useMoneyCurrency } from '@/components/money/MoneyProvider.js';

export const Route = createFileRoute('/_player/promos/history')({
  beforeLoad: requireAuth,
  // `tab` is the bonus-hub tab the player came from, and where the back button
  // returns to. Absent = every claim.
  validateSearch: z.object({ tab: z.enum(HISTORY_TABS).optional().catch(undefined) }),
  component: PromoHistoryScreen,
});

function PromoHistoryScreen() {
  const { t } = useTranslation('promo');
  const { t: tSpin } = useTranslation('spin');
  const navigate = Route.useNavigate();
  const { tab } = Route.useSearch();
  const currency = useMoneyCurrency();
  const onBack = () => void navigate({ to: '/promos', search: { tab } });

  if (tab === 'spin') {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={tSpin('history.title')} onBack={onBack} />
        <SpinHistoryList currency={currency} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('history.title')} onBack={onBack} />
      <ClaimHistory tab={tab} currency={currency} />
    </div>
  );
}

function ClaimHistory({ tab, currency }: { tab: HistoryTab | undefined; currency: string }) {
  const claimsQuery = useQuery({
    queryKey: PROMO_QUERY_KEYS.myClaims(),
    queryFn: () => fetchMyClaims(),
    staleTime: 30_000,
  });

  // A coupon can grant any bonus kind and the claim does not say it came from
  // one, so the redeem tab shows every claim rather than a guessed subset.
  const byTab = (kind: string | null | undefined) =>
    !tab || tab === 'redeem' || promoGroupOf(kind) === tab;
  const history = (claimsQuery.data?.items ?? []).filter(
    (c) => bucketOf(c.status) === 'history' && byTab(c.promotionKind),
  );

  return <MyClaimsList claims={history} currency={currency} isLoading={claimsQuery.isPending} />;
}
